import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Position, CreatePositionRequest, UpdatePositionRequest } from '@arrowise/shared';
import { positionService } from '../services/positionService';
import { toast } from 'sonner';

interface PositionsState {
    // State
    positions: Position[];
    isLoading: boolean;
    error: string | null;

    // Actions
    loadPositions: (teamId?: string) => Promise<void>;
    createPosition: (data: CreatePositionRequest) => Promise<Position | null>;
    updatePosition: (id: string, data: UpdatePositionRequest) => Promise<Position | null>;
    deletePosition: (id: string) => Promise<boolean>;
    getPositionById: (id: string) => Position | undefined;
    clearError: () => void;
}

export const usePositionsStore = create<PositionsState>()(
    devtools(
        (set, get) => ({
            // Initial state
            positions: [],
            isLoading: false,
            error: null,

            // Load positions for user or team
            loadPositions: async (teamId?: string) => {
                set({ isLoading: true, error: null });
                try {
                    const positions = await positionService.listPositions(teamId);
                    set({ positions, isLoading: false });
                } catch (error: any) {
                    const message = error?.message || 'Failed to load positions';
                    set({ error: message, isLoading: false });
                    toast.error(message);
                }
            },

            // Create a new position
            createPosition: async (data: CreatePositionRequest) => {
                set({ isLoading: true, error: null });
                try {
                    const newPosition = await positionService.createPosition(data);
                    set((state) => ({
                        positions: [...state.positions, newPosition],
                        isLoading: false
                    }));
                    toast.success('Position created successfully');
                    return newPosition;
                } catch (error: any) {
                    const message = error?.message || 'Failed to create position';
                    set({ error: message, isLoading: false });
                    toast.error(message);
                    return null;
                }
            },

            // Update an existing position
            updatePosition: async (id: string, data: UpdatePositionRequest) => {
                set({ isLoading: true, error: null });
                try {
                    const updatedPosition = await positionService.updatePosition(id, data);
                    set((state) => ({
                        positions: state.positions.map(pos =>
                            pos.id === id ? updatedPosition : pos
                        ),
                        isLoading: false
                    }));
                    toast.success('Position updated successfully');
                    return updatedPosition;
                } catch (error: any) {
                    const message = error?.message || 'Failed to update position';
                    set({ error: message, isLoading: false });
                    toast.error(message);
                    return null;
                }
            },

            // Delete a position
            deletePosition: async (id: string) => {
                set({ isLoading: true, error: null });
                try {
                    const result = await positionService.deletePosition(id);
                    if (result.deleted) {
                        set((state) => ({
                            positions: state.positions.filter(pos => pos.id !== id),
                            isLoading: false
                        }));
                        toast.success(`Position deleted successfully. ${result.sessionsDeleted} session(s) were also deleted.`);
                        return true;
                    } else {
                        throw new Error('Failed to delete position');
                    }
                } catch (error: any) {
                    const message = error?.message || 'Failed to delete position';
                    set({ error: message, isLoading: false });
                    toast.error(message);
                    return false;
                }
            },

            // Get position by ID from local state
            getPositionById: (id: string) => {
                return get().positions.find(pos => pos.id === id);
            },

            // Clear error state
            clearError: () => set({ error: null }),
        }),
        {
            name: 'positions-store',
        }
    )
);
